import React, { useId, useMemo, useCallback } from "react"
import { curveMonotoneX } from "@visx/curve"
import { AreaClosed, Line, Bar, LinePath } from "@visx/shape"
import { useTooltip } from "@visx/tooltip"
import { localPoint } from "@visx/event"
import { LinearGradient } from "@visx/gradient"
import { GridRows } from "@visx/grid"
import { ParentSize } from "@visx/responsive"
import type { TimeSeriesDataPoint, ChartMargin, TimeRange } from "../types"
import { CHART_COLORS, DEFAULT_MARGIN } from "../theme"
import { getTimeX, getTimeY, getBisector } from "../utils/dataUtils"
import { makeTimeRangeTickFormatter } from "../utils/formatters"
import { makeTimeScale, makeLinearScale } from "../utils/scales"
import { FrostedGlassTooltip } from "../components/FrostedGlassTooltip"
import { StandardAxisLeft, StandardAxisBottom } from "../components/ChartAxes"

export type AreaProps = {
    width: number
    height: number
    data: TimeSeriesDataPoint[]
    margin?: ChartMargin
    range: TimeRange
}

const InnerChart = ({ width, height, data, margin = DEFAULT_MARGIN, range }: AreaProps) => {
    const gradientId = useId()
    const clipId = useId()
    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipTop = 0,
        tooltipLeft = 0,
    } = useTooltip<TimeSeriesDataPoint>()

    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const xScale = useMemo(
        () =>
            makeTimeScale({
                data,
                accessor: getTimeX,
                rangeStart: margin.left,
                rangeEnd: innerWidth + margin.left,
            }),
        [data, margin.left, innerWidth]
    )

    const yScale = useMemo(
        () =>
            makeLinearScale({
                data,
                accessor: getTimeY,
                rangeStart: innerHeight + margin.top,
                rangeEnd: margin.top,
                minValue: 0.5,
            }),
        [data, margin.top, innerHeight]
    )

    const tickFormatter = useMemo(() => makeTimeRangeTickFormatter(range), [range])

    const tickValues = useMemo(
        () =>
            range === "30d"
                ? data.filter((_, i) => (i + 1) % 3 === 0).map((d) => new Date(getTimeX(d)))
                : undefined,
        [range, data]
    )

    const numTicks = range === "all" ? 6 : undefined

    const handleTooltip = useCallback(
        (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
            const { x } = localPoint(event) || { x: 0 }
            const x0 = xScale.invert(x).getTime()
            const index = getBisector(data, x0, getTimeX)
            const d0 = data.at(index - 1)
            const d1 = data.at(index)
            const d = !d0 ? d1 : !d1 ? d0 : x0 - getTimeX(d0) > getTimeX(d1) - x0 ? d1 : d0
            if (!d) return
            showTooltip({
                tooltipData: d,
                tooltipLeft: xScale(getTimeX(d)),
                tooltipTop: yScale(getTimeY(d)),
            })
        },
        [showTooltip, yScale, xScale, data]
    )

    if (width < 10) return null

    return (
        <div style={{ position: "relative", width, height }}>
            <svg width={width} height={height}>
                <rect width={width} height={height} fill={CHART_COLORS.background} rx={4} />

                <LinearGradient
                    id={gradientId}
                    from={CHART_COLORS.accent}
                    to={CHART_COLORS.accent}
                    fromOpacity={0.1}
                    toOpacity={0}
                />

                <GridRows
                    left={margin.left}
                    scale={yScale}
                    width={innerWidth}
                    stroke={CHART_COLORS.grid}
                    strokeOpacity={CHART_COLORS.gridOpacity}
                    pointerEvents="none"
                />

                <StandardAxisLeft scale={yScale} left={margin.left} numTicks={6} />
                <StandardAxisBottom
                    scale={xScale}
                    top={innerHeight + margin.top}
                    tickFormat={tickFormatter}
                    tickValues={tickValues}
                    numTicks={numTicks}
                />

                <g
                    key={range}
                    className="motion-safe:animate-bar-grow origin-bottom [transform-box:fill-box]"
                    clipPath={`url(#${clipId})`}
                >
                    <AreaClosed<TimeSeriesDataPoint>
                        data={data}
                        x={(d) => xScale(getTimeX(d))}
                        y={(d) => yScale(getTimeY(d))}
                        yScale={yScale}
                        fill={`url(#${gradientId})`}
                        curve={curveMonotoneX}
                    />
                    <LinePath<TimeSeriesDataPoint>
                        data={data}
                        x={(d) => xScale(getTimeX(d))}
                        y={(d) => yScale(getTimeY(d))}
                        stroke={CHART_COLORS.accent}
                        strokeWidth={1.5}
                        curve={curveMonotoneX}
                    />
                </g>

                <Bar
                    x={margin.left}
                    y={margin.top}
                    width={innerWidth}
                    height={innerHeight}
                    fill="transparent"
                    onMouseMove={handleTooltip}
                    onTouchMove={handleTooltip}
                    onMouseLeave={() => hideTooltip()}
                />

                {tooltipData && (
                    <g>
                        <Line
                            from={{ x: tooltipLeft, y: margin.top }}
                            to={{ x: tooltipLeft, y: innerHeight + margin.top }}
                            stroke={CHART_COLORS.accent}
                            strokeWidth={1}
                            pointerEvents="none"
                        />
                        <circle
                            cx={tooltipLeft}
                            cy={tooltipTop}
                            r={4}
                            fill={CHART_COLORS.accent}
                            stroke={CHART_COLORS.background}
                            strokeWidth={2}
                            pointerEvents="none"
                        />
                    </g>
                )}
            </svg>

            {tooltipData && (
                <FrostedGlassTooltip top={tooltipTop} left={tooltipLeft}>
                    <div className="text-xs text-gray-400">
                        {new Intl.DateTimeFormat("en-US", {
                            month: "short",
                            ...(range === "all" || range === "1y"
                                ? { year: "numeric" }
                                : { day: "numeric" }),
                        }).format(new Date(getTimeX(tooltipData)))}
                    </div>
                    <div className="font-medium">
                        {getTimeY(tooltipData).toFixed(2)}{" "}
                        {getTimeY(tooltipData) === 1 ? "hour" : "hours"}
                    </div>
                </FrostedGlassTooltip>
            )}
        </div>
    )
}

export const PilotTrackingActivityChart = (props: Omit<AreaProps, "width" | "height">) => (
    <div style={{ width: "100%", height: "100%" }}>
        <ParentSize>
            {({ width, height }) => <InnerChart {...props} width={width} height={height} />}
        </ParentSize>
    </div>
)
